import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, Row } from "antd/es/grid";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import PermissionName from "@mapmycustomers/shared/enum/PermissionName";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import CalculatedFieldInfoIcon from "@app/component/CalculatedFieldInfoIcon";
import { hasPermission } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { isCalculatedField } from "@app/util/fieldModel/impl/assert";
import useToggleBooleanAfterTimeout from "@app/util/hook/useToggleBooleanAfterTimeout";

import messages from "../../messages";
import { downloadEntities } from "../../store/actions";
import {
  getField,
  getFunnelData,
  getGroupData,
  getSelectedUsers,
  getTotalValue,
  getUserData,
  isDataLoading,
  isDownloading,
} from "../../store/selectors";
import isGroupedField from "../../util/isGroupedField";
import Breakdown from "../Breakdown";
import FieldSelector from "../FieldSelector";
import Funnels from "../Funnels";
import Groups from "../Groups";
import NoData from "../NoData";
import UserSelector from "../UserSelector";

import styles from "./Data.module.scss";

interface Props {
  canDownload?: boolean;
  downloadEntities: typeof downloadEntities.request;
  downloading: boolean;
  field: IField | undefined;
  loading: boolean;
  noBreakdownData: boolean;
  noGroupData: boolean;
  totalValue: number;
}

const Data: React.FC<Props> = ({
  canDownload,
  downloadEntities,
  downloading,
  field,
  loading,
  noBreakdownData,
  noGroupData,
}) => {
  const intl = useIntl();
  const analyticIssuer = useAnalytics();

  const [firstLoading, setFirstLoading] = useState(true);
  useEffect(() => {
    if (firstLoading && !loading) {
      setFirstLoading(false);
    }
  }, [firstLoading, loading]);

  const showLoading = useToggleBooleanAfterTimeout(1000, false, loading, true) || firstLoading;

  const handleDownload = useCallback(() => {
    downloadEntities();
    analyticIssuer.clicked(["Export"]);
  }, [analyticIssuer, downloadEntities]);

  if (!field) {
    return null;
  }
  return (
    <div className={styles.container}>
      <Row align="middle" className={styles.summary} justify="space-between">
        <Col span={18}>
          <FieldSelector />
        </Col>
        <Col span={4}>
          <Row align="middle" justify="space-between">
            {isCalculatedField(field) && (
              <Col>
                <CalculatedFieldInfoIcon customField={field.customFieldData} />
              </Col>
            )}
            {!canDownload ? null : downloading ? (
              <LoadingSpinner mini />
            ) : (
              <Tooltip title={intl.formatMessage(messages.export)}>
                <Button
                  icon={<FontAwesomeIcon className={styles.download} icon={faDownload} />}
                  iconOnly
                  onClick={handleDownload}
                  type="text"
                />
              </Tooltip>
            )}
          </Row>
        </Col>
      </Row>
      {showLoading ? (
        <LoadingSpinner global />
      ) : (
        <div>
          {isGroupedField(field) ? (
            <>
              <UserSelector />
              {noGroupData ? (
                <NoData />
              ) : field.hasFeature(FieldFeature.FUNNEL_FIELD) ? (
                <Funnels />
              ) : (
                <Groups />
              )}
            </>
          ) : noBreakdownData ? (
            <NoData />
          ) : (
            <Breakdown />
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  canDownload: hasPermission(state)(PermissionName.EXPORT_DOWNLOAD),
  downloading: isDownloading(state),
  field: getField(state),
  loading: isDataLoading(state),
  noBreakdownData: getUserData(state).length === 0,
  noGroupData:
    getSelectedUsers(state).length === 0 ||
    (getGroupData(state).length === 0 && getFunnelData(state).length === 0),
  totalValue: getTotalValue(state),
  userData: getUserData(state),
});

const mapDispatchToProps = {
  downloadEntities: downloadEntities.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Data);
